import { Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { BridgeAPIService } from '../bridge-api.service';

export interface SocialChoice {
  key: string;
  display: string;
}

export interface SocialConfig {
  services: string;
}

const NICKNAME_PATTERN =
  /spotlight-[a-zA-Z0-9]+-storage-([a-zA-Z0-9]+).semantria.com|([a-zA-Z0-9]+)-spotlight-[a-zA-Z0-9]+-storage.semantria.com|spotlight-([a-zA-Z0-9]+).semantria.com|([a-zA-Z0-9]+)-spotlight.semantria.com|spotlight-([a-zA-Z0-9]+)-(?:au|eu).semantria.com|([a-zA-Z0-9]+)-spotlight-(?:au|eu).semantria.com/;
const INMOMENT_NICKNAME_PATTERN = /spotlight(?:.demo)?.inmoment.(?:com|eu|com\.au)/;

@EnableClassTelemetry()
@Injectable()
export class SocialLoginService extends BridgeAPIService {
  get nickname(): string {
    return getNickname.bind(this)();
  }

  async getChoices(): Promise<SocialChoice[]> {
    if (!this.nickname) {
      return [];
    }
    const url = `/session/_sso_config?nickname=${encodeURIComponent(this.nickname)}`;

    const socialConfig = await this.get<SocialConfig>(url);

    const socialServices = socialConfig.services;
    const choices = socialServices
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => {
        const m = /(.+)<(.+)>/.exec(s);
        if (!m) {
          return { key: s, display: s } as SocialChoice;
        }
        return { key: m[2], display: m[1] } as SocialChoice;
      });
    return choices;
  }

  login(key: string) {
    let url = `${this.getBaseUrl()}/session/${key}/signin`;
    let pad = '?';
    if (this.nickname) {
      url += `${pad}nickname=${encodeURIComponent(this.nickname)}`;
      pad = '&';
    }
    const programId = this.getParam('programId');
    if (programId) {
      url += `${pad}serviceToken=${encodeURIComponent(programId)}`;
      pad = '&';
    }

    const groupId = this.getParam('groupId');
    if (groupId) {
      url += `${pad}groupId=${encodeURIComponent(groupId)}`;
      pad = '&';
    }

    const dashboardAlias = this.getParam('dashboardAlias');
    if (dashboardAlias) {
      url += `${pad}dashboardAlias=${encodeURIComponent(dashboardAlias)}`;
      pad = '&';
    }

    const appVolatile = (window as any).appVolatile;
    if (appVolatile?.redirectTo) {
      url += `${pad}deepLink=${encodeURIComponent(appVolatile.redirectTo)}`;
      pad = '&';
    }

    window.location.assign(url);
  }

  private getParam(name: string): string {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }
}

export function setNickname(nickname: string) {
  if (!nickname) {
    this.localStorage.removeItem('nickname');
  } else {
    this.localStorage.setItem('nickname', nickname, 'local', true);
  }
}

export function getNickname(): string {
  let nickname = getNicknameFromOrigin(window.location.origin);
  if (!nickname) {
    nickname = getNicknameFromParams(window.location.search);
  }
  if (!nickname) {
    nickname = this.localStorage.getItem('nickname');
  }
  return nickname;
}

export function getNicknameFromOrigin(origin: string): string {
  let nickname = '';
  if (INMOMENT_NICKNAME_PATTERN.exec(origin)) {
    return 'inmoment';
  }
  const match = NICKNAME_PATTERN.exec(origin);
  if (match) {
    nickname = match[1] || match[2] || match[3] || match[4] || match[5] || match[6];
  }
  if (nickname.length <= 3) {
    // reserved for eu, au, etc
    nickname = '';
  }
  return nickname;
}

export function getNicknameFromParams(search: string): string {
  let nickname = '';
  const params = new URLSearchParams(search);
  if (params.get('nickname')) {
    nickname = params.get('nickname');
  }
  return nickname;
}
